import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRODUCT_FEATURES, SOLUTIONS } from '../data/featurePages';
import logoImage from '../assets/logo_small.png';

// ── SiteNav ────────────────────────────────────────────
export function SiteNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<'product' | 'solutions' | null>(null);
  const [openMobileSection, setOpenMobileSection] = useState<'product' | 'solutions' | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);

  // Initialise dark mode from localStorage / system preference
  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const dark = stored !== null
      ? stored === 'true'
      : window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(dark);
    document.documentElement.classList.toggle('dark', dark);
  }, []);

  // Close mega-menu on outside click or ESC
  useEffect(() => {
    if (!openMenu) return;
    const onMouseDown = (e: MouseEvent) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMenu(null);
    setOpenMobileSection(null);
  }, [location.pathname]);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('darkMode', String(next));
  };

  const handleLogin = () => { navigate('/login'); setMobileMenuOpen(false); };
  const handleStartTrial = () => { navigate('/signup'); setMobileMenuOpen(false); };

  const handlePricing = () => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-700/50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex justify-between items-center">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={logoImage}
            alt="Visto Logo"
            width="40"
            height="40"
            className="w-10 h-10 object-contain"
          />
          <span className="text-2xl font-extrabold text-slate-900 dark:text-white">Visto</span>
        </Link>

        {/* ── Desktop Menu ── */}
        <div className="hidden md:flex items-center gap-6" ref={megaMenuRef}>

          {/* Product mega-menu */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === 'product' ? null : 'product')}
              className={`flex items-center gap-1 font-semibold transition-colors ${openMenu === 'product' ? 'text-emerald-500' : 'text-slate-600 dark:text-slate-300 hover:text-emerald-500'}`}
            >
              Product
              <span className={`material-symbols-outlined text-base leading-none transition-transform duration-200 ${openMenu === 'product' ? 'rotate-180 text-emerald-500' : ''}`}>expand_more</span>
            </button>

            {openMenu === 'product' && (
              <div className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-[580px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl z-[60] p-5">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-l border-t border-slate-200 dark:border-slate-700 rotate-45" />
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 px-1">Features</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {PRODUCT_FEATURES.map(f => (
                    <Link
                      key={f.slug}
                      to={`/features/${f.slug}`}
                      onClick={() => setOpenMenu(null)}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-emerald-50 dark:hover:bg-slate-800 group transition-colors"
                    >
                      <div className="w-9 h-9 bg-emerald-50 dark:bg-slate-800 rounded-lg flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30 transition-colors">
                        <span className="material-symbols-outlined text-lg">{f.navIcon}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors truncate">{f.navTitle}</p>
                          {f.isNew && <span className="shrink-0 text-[9px] font-extrabold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full leading-none">NEW</span>}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug mt-0.5 line-clamp-2">{f.navDescription}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <p className="text-xs text-slate-400">No credit card required · 7-day free trial</p>
                  <button
                    onClick={() => { handleStartTrial(); setOpenMenu(null); }}
                    className="flex items-center gap-1 text-xs font-extrabold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 transition-colors"
                  >
                    Start Free Trial
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Industries dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === 'solutions' ? null : 'solutions')}
              className={`flex items-center gap-1 font-semibold transition-colors ${openMenu === 'solutions' ? 'text-emerald-500' : 'text-slate-600 dark:text-slate-300 hover:text-emerald-500'}`}
            >
              Industries
              <span className={`material-symbols-outlined text-base leading-none transition-transform duration-200 ${openMenu === 'solutions' ? 'rotate-180 text-emerald-500' : ''}`}>expand_more</span>
            </button>

            {openMenu === 'solutions' && (
              <div className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-[440px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl z-[60] p-5">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-l border-t border-slate-200 dark:border-slate-700 rotate-45" />
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 px-1">By Industry</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {SOLUTIONS.map(s => (
                    <Link
                      key={s.slug}
                      to={`/solutions/${s.slug}`}
                      onClick={() => setOpenMenu(null)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-emerald-50 dark:hover:bg-slate-800 group transition-colors"
                    >
                      <div className="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-base text-emerald-600 dark:text-emerald-400">{s.icon}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors truncate">{s.title}</p>
                          {s.isPopular && <span className="shrink-0 text-[9px] font-extrabold bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full leading-none">HOT</span>}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug mt-0.5 truncate">{s.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button onClick={handlePricing} className="text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors font-semibold">{t('nav.pricing')}</button>

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:text-emerald-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {isDarkMode
              ? <span className="material-symbols-outlined text-xl">light_mode</span>
              : <span className="material-symbols-outlined text-xl">dark_mode</span>}
          </button>

          <button onClick={handleLogin} className="text-slate-900 dark:text-white hover:text-emerald-500 font-bold transition-colors">
            {t('nav.login')}
          </button>
          <button
            onClick={handleStartTrial}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-7 py-2.5 rounded-full font-extrabold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5 ring-2 ring-emerald-400/30 animate-pulse-subtle"
          >
            {t('nav.startTrial')}
          </button>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={handleStartTrial}
            className="bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white px-4 py-2 rounded-full font-extrabold text-sm shadow-md shadow-emerald-500/30 transition-all"
          >
            {t('nav.startTrial')}
          </button>
          <button
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            className="p-2 text-slate-900 dark:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="material-symbols-outlined text-3xl" aria-hidden="true">menu</span>
          </button>
        </div>
      </div>

      {/* ── Mobile Menu Dropdown ── */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-4 rounded-2xl absolute left-4 right-4 z-50 bg-white dark:bg-slate-900 backdrop-blur-xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden">

          {/* Product accordion */}
          <button
            onClick={() => setOpenMobileSection(openMobileSection === 'product' ? null : 'product')}
            className="flex items-center justify-between w-full px-5 py-4 text-left border-b border-slate-100 dark:border-slate-800"
          >
            <span className="font-bold text-slate-900 dark:text-white">Product</span>
            <span className={`material-symbols-outlined text-slate-400 transition-transform ${openMobileSection === 'product' ? 'rotate-180' : ''}`}>expand_more</span>
          </button>
          {openMobileSection === 'product' && (
            <div className="px-3 pb-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              {PRODUCT_FEATURES.map(f => (
                <Link
                  key={f.slug}
                  to={`/features/${f.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <span className="material-symbols-outlined text-base">{f.navIcon}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">{f.navTitle}</p>
                      {f.isNew && <span className="text-[9px] font-extrabold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{f.navDescription}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Industries accordion */}
          <button
            onClick={() => setOpenMobileSection(openMobileSection === 'solutions' ? null : 'solutions')}
            className="flex items-center justify-between w-full px-5 py-4 text-left border-b border-slate-100 dark:border-slate-800"
          >
            <span className="font-bold text-slate-900 dark:text-white">Industries</span>
            <span className={`material-symbols-outlined text-slate-400 transition-transform ${openMobileSection === 'solutions' ? 'rotate-180' : ''}`}>expand_more</span>
          </button>
          {openMobileSection === 'solutions' && (
            <div className="px-3 pb-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              {SOLUTIONS.map(s => (
                <Link
                  key={s.slug}
                  to={`/solutions/${s.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="w-8 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-base text-emerald-600 dark:text-emerald-400">{s.icon}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">{s.title}</p>
                      {s.isPopular && <span className="text-[9px] font-extrabold bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full">HOT</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Flat links */}
          <div className="p-4 space-y-1">
            <button
              onClick={handlePricing}
              className="flex items-center gap-2 w-full text-left text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors font-semibold py-2 px-2"
            >
              <span className="material-symbols-outlined text-base">sell</span>
              {t('nav.pricing')}
            </button>
            <button
              onClick={toggleDarkMode}
              className="flex items-center gap-2 w-full text-left text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors font-semibold py-2 px-2"
            >
              <span className="material-symbols-outlined text-base">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>

          <div className="p-4 pt-0 space-y-3 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={handleLogin}
              className="w-full py-3 rounded-full font-bold text-slate-900 dark:text-white border-2 border-slate-200 dark:border-slate-700 hover:border-emerald-400 transition-colors"
            >
              {t('nav.login')}
            </button>
            <button
              onClick={handleStartTrial}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full font-extrabold shadow-lg"
            >
              {t('nav.startTrial')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
